/**
 * Author registry.
 *
 * E-E-A-T requires a real, attributable person behind YMYL content (money,
 * immigration, legal). Every field here is surfaced on-page AND emitted as
 * schema.org Person data — so it must be true. A reviewer who finds an invented
 * credential discounts the whole site.
 *
 * The bio and credentials below are a starting draft — read them as claims being
 * made publicly under a real name, and edit anything that overstates. Adding
 * `sameAs` (LinkedIn, personal site) measurably strengthens the Person entity;
 * only add profiles that actually exist.
 */

export interface Author {
  id: string;
  name: string;
  nameKo?: string;
  /** Short role shown next to the name. */
  title: string;
  /** 1–2 sentence bio establishing why this person can write on this topic. */
  bio: string;
  /** Lived-experience signal — the "E" that thin content can't fake. */
  credentials: string[];
  avatar?: string;
  /** Author archive page on this site. Required for the Person schema URL. */
  profileUrl: string;
  /** External profiles used as `sameAs` in structured data. */
  sameAs?: string[];
}

/** Editorial reviewer — a second named human who checked the facts. */
export interface Reviewer {
  name: string;
  title: string;
  /** What they actually verified. Vague claims here are a liability. */
  scope: string;
}

export const AUTHORS: Record<string, Author> = {
  'tim-park': {
    id: 'tim-park',
    name: 'Tim Park',
    title: 'Editor, KoreanHub Guides',
    bio: 'Tim Park is the editor of KoreanHub, where they research and maintain guides on money transfers, visas, and settlement for Korean communities abroad. Every guide is checked against primary government and provider sources before publication.',
    credentials: [
      'Sources every figure from primary government or provider documentation',
      'Re-verifies fee and rate data on a published schedule',
      'Founder and editor of KoreanHub',
    ],
    profileUrl: '/about',
  },
};

/** Default byline used when a guide does not name a specific author. */
export const DEFAULT_AUTHOR_ID = 'tim-park';

export function getAuthor(id: string): Author {
  return AUTHORS[id] ?? AUTHORS[DEFAULT_AUTHOR_ID];
}
